<template>
  <v-container class="invs">
    <portal to="content-before">
      <OrsInnerToolbar
        title="Notas Fiscais e Boletos"
        title-icon="mdi-barcode"
        extended
        :extension-height="toolbarExtensionHeight"
      >
        <template #extension>
          <div class="d-flex flex-column">
            <small class="mt-4 primary--text">
              Receber notas fiscais e boletos por email
            </small>
            <v-checkbox
              v-model="emailSubscribed"
              label="Estou de acordo em receber a nota fiscal,
                    boleto e documentos relativos a comprovação
                    dos serviços prestados, no Portal do Cliente no
                    Site Orsegups em meu email, conforme o termo de
                    acordo e competências."
              :disabled="updatingSubscription"
              :indeterminate="emailSubscribed === undefined"
            >
              Receber notas fiscais e boletos por email
            </v-checkbox>
            <v-dialog
              :value="visibleDialog === 'email'"
              persistent
            >
              <v-card>
                <v-card-title>Receber documentos por e-mail</v-card-title>
                <v-card-text>
                  Adicione seu e-mail para receber nota fiscal, boleto e documentos relativos a
                  comprovação dos serviços prestados, no Portal do Cliente Orsegups.
                  <br>
                  Para adicionar mais de um e-mail, use ; para separar.
                  <br>
                  Exemplo: e-mail@teste.com.br;e-mail@teste.com.br
                  <br>
                  Obs: Para remover um e-mail já cadastrado, é necessario abrir uma solicitação via formulario.
                  <v-form
                    v-model="emailDialogFormValid"
                    class="pa-4 pt-8"
                  >
                    <v-text-field
                      v-model="emailDialogFormEmail"
                      label="E-mail"
                      placeholder="Digite seu e-mail"
                      type="email"
                      required
                    />
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    :disabled="updatingSubscription"
                    @click="setVisibleDialog(null)"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    rounded
                    large
                    color="primary"
                    :disabled="(!emailDialogFormValid||updatingSubscription)"
                    :loading="updatingSubscription"
                    @click="emailDialogFormValid && createSubscription(emailDialogFormEmail)"
                  >
                    Confirmar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              :value="visibleDialog === 'cancel'"
              persistent
              max-width="400"
            >
              <v-card>
                <v-card-title>Cancelar envio de documentos</v-card-title>
                <v-card-text>
                  Cancelar envio de nota fiscal, boleto e documentos relativos
                  a comprovação dos serviços prestados, no Portal do Cliente Orsegups
                  em seu e-mail?
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    :disabled="updatingSubscription"
                    @click="setVisibleDialog(null)"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    rounded
                    large
                    color="primary"
                    :disabled="updatingSubscription"
                    :loading="updatingSubscription"
                    @click="removeSubscription"
                  >
                    Sim
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </template>
      </OrsInnerToolbar>
    </portal>
    <v-row
      v-if="isLoading && currentUser.cpfCnpj !== '53806395934'"
      justify="center"
      class="mt-8"
    >
      <v-progress-circular indeterminate />
    </v-row>
    <v-row
      v-else-if="noResults || currentUser.cpfCnpj === '53806395934'"
      justify="center"
      class="mt-8"
    >
      <h3>
        Nenhuma Nota Fiscal ou Boleto encontrado no período de 6 Meses.
      </h3>
    </v-row>
    <template v-else>
      <OrsTimeline :dense="$vuetify.breakpoint.smAndDown">
        <OrsTimelineItem
          v-for="(period, periodIdx) in limitedPeriods"
          :key="periodIdx"
          :title="`Competência ${period.competencia}  `"
          :color="getPeriodColor(period)"
        >
          <v-skeleton-loader
            #default
            type="list-item-two-line@3"
          >
            <v-alert
              v-if="noResults"
              type="error"
            >
              Erro ao carregar notas.
            </v-alert>
            <v-row
              v-for="(company, companyIdx) in period.invoices"
              v-else
              :key="companyIdx"
            >
              <v-col
                cols="11"
                class="invs-period-border"
                style="color:#0000008a"
              >
                {{ company.nomeEmpresa }}
                {{ company.cnpj }}
                <v-row
                  v-for="(inv, invIdx) in company.listaDocumento"
                  :key="invIdx"
                  :class="getInvoiceClass(inv)"
                  align="center"
                >
                  <v-col
                    cols="8"
                    md="3"
                    lg="2"
                    style="max-width: 22%;"
                  >
                    <b
                      style="color: black;font-size:9px;"
                    >
                      NFS-e
                    </b>
                    <br>
                    <sub
                      style="color: black;font-size:9px;"
                    >
                      {{ inv.numDfs }}
                    </sub>
                  </v-col>
                  <v-col
                    cols="8"
                    md="3"
                    lg="2"
                    style="max-width: 27%;"
                  >
                    <b
                      style="color: black;font-size:9px;"
                    >
                      EMISSÃO
                    </b>
                    <br>
                    <sub
                      style="color: black;font-size:9px;"
                    >
                      {{ inv.emissao }}
                    </sub>
                  </v-col>
                  <v-col
                    cols="8"
                    md="3"
                    lg="2"
                    style="max-width: 27%;"
                  >
                    <b
                      style="color: black;font-size:9px;"
                    >
                      CHAVE
                    </b>
                    <br>
                    <sub
                      style="color: black;font-size:9px;"
                    >
                      {{ inv.codver }}
                    </sub>
                  </v-col>
                  <v-col
                    cols="2"
                    md="3"
                    lg="2"
                    style="max-width: 30%;"
                  >
                    <b
                      style="color: black;font-size:10px;"
                    >
                      VALOR
                    </b>
                    <br>
                    <sub
                      style="color: black;font-size:10px;"
                    >
                      {{ formatValor(inv.valor) }}
                    </sub>
                  </v-col>
                  <v-col
                    v-if="inv.sitTit=='LQ'"
                    cols="6"
                    md="4"
                    lg="6"
                    style="max-width: 30%;"
                  >
                    <span
                      data-v-1e347e04=""
                      class="v-chip v-chip--label theme--light
                          v-size--x-small teal white--text
                          ors-tml-i-title-status"
                    ><span
                      class="v-chip__content"
                      style="font-size:9px"
                    >
                      LIQUIDADO
                    </span></span>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="6"
                    style="max-width: 30%;"
                  >
                    <span
                      data-v-1e347e04=""
                      class="v-chip v-chip--label theme--light
                    v-size--x-small"
                      style="background-color: rgb(231, 130, 3);color:white;"
                    >
                      PENDENTE
                    </span>
                  </v-col>
                  <v-col
                    v-if="inv.linkNfseDownload == null"
                    class=""
                    cols="6"
                    md="4"
                    lg="3"
                    style="width: 17%;max-width: 18%;"
                  />
                  <v-col
                    v-else
                    class=""
                    cols="6"
                    md="4"
                    lg="3"
                    style="width: 17%;max-width: 18%;"
                  >
                    <a
                      @click.prevent="downloadURI(inv.linkNfseDownload,
                                                  'NFSe'+inv.linkNfseDownload
                                                    .replace(/.*NFSe(.*).*/, '$1'))"
                    >
                      <img
                        data-v-22ba47ca=""
                        src="https://orsegups-portalcliente.s3-sa-east-1.amazonaws.com/Orsegups/pdfIcon.png"
                        class="odash-dg-img"
                        style="width:50px;"
                      >
                    </a>
                  </v-col>
                  <v-col
                    v-if="inv.linkNfse == null"
                    class=""
                    cols="6"
                    md="4"
                    lg="3"
                    style="width: 17%;max-width: 18%;"
                  >
                    <a
                      :href="inv.linkNfse"
                      target="_blank"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">NFSE</font></a>
                  </v-col>
                  <v-col
                    v-else
                    class=""
                    cols="6"
                    md="4"
                    lg="3"
                    style="width: 17%;max-width: 18%;"
                  >
                    <a
                      :href="inv.linkNfse"
                      target="_blank"
                      style="text-decoration: none;"
                    ><font size="1">NFSE</font></a>
                  </v-col>
                  <v-col
                    v-if="inv.linkBoleto === null"
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font
                       v-if="inv.sitTit==='AB'"
                       size="1"
                     >BOLETO
                     </font>
                      <font
                        v-else
                        size="1"
                      >PAGO.
                      </font>
                    </a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkBoleto,
                                                  'BOLETO'+inv.linkBoleto
                                                    .replace(/.*BOLETO(.*).*/, '$1'))"
                    ><font size="1">BOLETO</font></a>
                  </v-col>
                  <v-col
                    v-if="inv.linkXML == null"
                    cols="6"
                    md="4"
                    lg="1"
                    style="width: 16%;max-width: 30%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">XML</font></a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="1"
                    style="width: 16%;max-width: 30%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkXML, 'NFSe'+inv.rps+'.xml')"
                    ><font size="1">XML</font></a>
                  </v-col>
                  <v-col
                    v-if="inv.linkGPS == null"
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">GPS</font></a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkGPS,
                                                  'GPS'+inv.linkGPS.replace(/.*GPS(.*).*/, '$1'))"
                    ><font size="1">GPS</font></a>
                  </v-col>
                  <v-col
                    v-if="inv.linkDarfIRRF == null"
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">Darf IRRF</font></a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkDarfIRRF,
                                                  '_IRRF'+inv.linkDarfIRRF
                                                    .replace(/.*IRRF(.*).*/, '$1'))"
                    ><font size="1">Darf IRRF</font></a>
                  </v-col>
                  <v-col
                    v-if="inv.linkDarfPcc == null"
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">Darf PCC</font></a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkDarfPcc,
                                                  '_PCC'+inv.linkDarfPcc
                                                    .replace(/.*PCC(.*).*/, '$1'))"
                    ><font size="1">Darf PCC</font></a>
                  </v-col>

                  <v-col
                    v-if="inv.linkRPS == null"
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none;
                    cursor: not-allowed; opacity: 0.2; pointer-events: none;"
                    ><font size="1">RPS</font></a>
                  </v-col>
                  <v-col
                    v-else
                    cols="6"
                    md="4"
                    lg="2"
                    style="width: 24%;"
                  >
                    <a
                      href="#"
                      style="text-decoration: none; "
                      @click.prevent="downloadURI(inv.linkRPS,
                                                  'RPS'+inv.linkRPS.replace(/.*RPS(.*).*/, '$1'))"
                    ><font size="1">RPS</font></a>
                  </v-col>

                  <v-col
                    cols="6"
                    md="4"
                    lg="4"
                  >
                    <b
                      style="font-size:9px;color:black;"
                    >
                      RPS: {{ inv.rps }}
                      SÉRIE: {{ inv.serie }}
                    </b>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-skeleton-loader>
        </OrsTimelineItem>
      </OrsTimeline>
      <div
        v-if="!noMoreResults"
        class="d-flex justify-center ma-4"
      >
        <v-btn
          color="primary"
          elevation="0"
          rounded
          @click="loadMorePeriods"
        >
          Ver mais
        </v-btn>
      </div>
    </template>
  </v-container>
</template>
<script src="https://cdnjs.cloudflare.com/ajax/libs/FileSaver.js/2.0.0/FileSaver.min.js"></script>
<script>
import { createNamespacedHelpers } from 'vuex';
import OrsInnerToolbar from '@/components/OrsInnerToolbar.vue';
import OrsTimeline from '../components/OrsTimeline.vue';
import OrsTimelineItem from '@/components/OrsTimelineItem.vue';
import saveAs from '../lib/downloadFile';

const session = createNamespacedHelpers('session');
const emailSubscription = createNamespacedHelpers('emailSubscription');
const invoices = createNamespacedHelpers('views/invoices');

export default {
  name: 'Invoices',
  components: {
    OrsInnerToolbar,
    OrsTimeline,
    OrsTimelineItem,
  },
  data: () => ({
    emailDialogFormValid: true,
    emailDialogFormEmail: null,
  }),
  computed: {
    ...emailSubscription.mapState([
      'updatingSubscription',
      'visibleDialog',
    ]),
    ...emailSubscription.mapGetters([
      'isEmailSubscribed',
    ]),
    ...emailSubscription.mapGetters([
      'isEmailSubscribed',
    ]),
    emailSubscribed: {
      get() {
        if (this.visibleDialog) {
          return undefined;
        }
        return this.isEmailSubscribed;
      },
      set() {
        if (this.isEmailSubscribed) {
          this.setVisibleDialog('cancel');
        } else {
          this.$router.push({
            name: 'Terms',
            params: {
              redirect: this.$route,
              onAccept: () => this.setVisibleDialog('email'),
            },
          });
        }
      },
    },
    ...invoices.mapGetters([
      'isLoading',
      'noResults',
      'noMoreResults',
      'isInvoiceTooltipVisible',
    ]),
    ...invoices.mapGetters([
      'limitedPeriods',
    ]),
    ...session.mapState([
      'currentAccount',
      'currentUser',
    ]),
    toolbarExtensionHeight() {
      if (this.$vuetify.breakpoint.mdAndUp) return 100;
      return 200;
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.loadPeriods();
      vm.addAccountChangeListener(vm.loadPeriods);
      vm.fillEmailDialogFormEmail();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.removeAccountChangeListener(this.loadPeriods);
    next();
  },
  methods: {
    ...session.mapMutations([
      'addAccountChangeListener',
      'removeAccountChangeListener',
    ]),
    ...invoices.mapMutations([
      'setActiveTooltipInvoice',
    ]),
    ...invoices.mapActions([
      'loadPeriods',
      'loadMorePeriods',
      'loadDeepPeriods',
    ]),
    ...emailSubscription.mapMutations([
      'setVisibleDialog',
    ]),
    ...emailSubscription.mapActions([
      'createSubscription',
      'removeSubscription',
    ]),
    formatValor(valor) {
      if (!valor) return 'N/A';
      const nf = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
      return nf.format(parseFloat(valor));
    },
    isInvoiceUnpaid(inv) {
      return inv.sitTit !== 'LQ';
    },
    getInvoiceClass(inv) {
      return {
        'invs-period': true,
        'invs-period--unpaid': this.isInvoiceUnpaid(inv),
      };
    },
    getPeriodColor(per) {
      const sitMap = {
        AB: 'orange',
        LQ: 'teal',
      };
      return sitMap[per.situacaoTitulo];
    },
    getPeriodStatus(per) {
      const sitMap = {
        AB: 'PENDENTE',
        LQ: 'EFETUADO',
      };
      return sitMap[per.situacaoTitulo];
    },
    downloadURI(uri, name) {
      var oReq = new XMLHttpRequest();
      // The Endpoint of your server
      var URLToPDF = uri;
      oReq.open("GET", URLToPDF, true);
      oReq.responseType = "blob";
      oReq.onload = function() {
          var file = new Blob([oReq.response], {
              type: 'application/pdf'
          });
          saveAs(file, name);
      };

      oReq.send();
    },
    getNfMenu(inv) {
      const getObj = (title, prop) => ({ title, link: inv[prop] });
      const items = [
        getObj(`Emissor ${inv.cgcEmissor}`, 'emissor'),
      ];
      items.push(
        getObj(`RPS ${inv.rps}`, 'rps'),
        getObj(`SÉRIE ${inv.serie}`, 'serie'),
      );
      return items.filter(i => i.link && i.link !== 'N/A');
    },
    fillEmailDialogFormEmail() {
      if (this.currentUser.email) {
        this.emailDialogFormEmail = this.currentUser.email;
      }
    },
  },
};
</script>
<style lang="css" scoped>
.invs .invs-period:not(:first-child) .invs-period-border:before {
  content: ' ';
  display: block;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.16);
}
.invs .invs-period.invs-period--unpaid {
  color: var(--secondary-color-5);
}
.invs .invs-period .invs-period-actions {
  white-space: nowrap;
}
.invs .invs-period .invs-period-actions .invs-period-actions-action {
  color: var(--dash-color-3);
}
.invs-period-actions-menu /deep/ .v-list-item__icon .v-icon {
  color: var(--dash-color-3);
}
</style>
